import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import StatusBadge from '../common/StatusBadge';
import ConfirmDialog from '../common/ConfirmDialog';

const MODE_LABELS = {
  sequential: { label: 'Sequential', color: 'text-blue-400' },
  random: { label: 'Random', color: 'text-warning-400' },
  rotation: { label: 'Rotation', color: 'text-success-400' },
};

export default function PlaylistList() {
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmItem, setConfirmItem] = useState(null);

  useEffect(() => {
    fetchPlaylists();
  }, []);

  const fetchPlaylists = async () => {
    try {
      const { data } = await api.get('/api/playlists');
      setPlaylists(data);
    } catch (err) {
      console.error('[PlaylistList] Failed to fetch playlists:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (playlist) => {
    try {
      const { data } = await api.put(`/api/playlists/${playlist.id}`, {
        is_active: !playlist.is_active,
      });
      setPlaylists(playlists.map((p) =>
        p.id === playlist.id ? { ...p, is_active: data.is_active } : p
      ));
    } catch (err) {
      console.error('[PlaylistList] Toggle failed:', err);
    }
  };

  const handleDelete = async () => {
    if (!confirmItem) return;
    try {
      await api.delete(`/api/playlists/${confirmItem.id}`);
      setPlaylists(playlists.filter((p) => p.id !== confirmItem.id));
    } catch (err) {
      console.error('[PlaylistList] Delete failed:', err);
    } finally {
      setConfirmOpen(false);
      setConfirmItem(null);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <p className="text-dark-400">Loading playlists...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Playlists</h1>
        <Link to="/playlists/create" className="btn btn-primary btn-sm">
          + New Playlist
        </Link>
      </div>

      {playlists.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No playlists found. Create your first playlist to get started.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Mode</th>
                <th>Items</th>
                <th>Last Played</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {playlists.map((playlist) => {
                const modeInfo = MODE_LABELS[playlist.mode] || { label: playlist.mode || 'Unknown', color: 'text-dark-400' };
                return (
                  <tr key={playlist.id}>
                    <td className="font-medium text-dark-100">
                      <Link to={`/playlists/${playlist.id}`} className="hover:text-primary-400">
                        {playlist.name}
                      </Link>
                    </td>
                    <td>
                      <span className={`text-sm font-medium ${modeInfo.color}`}>{modeInfo.label}</span>
                    </td>
                    <td className="text-dark-300">{playlist.item_count || 0} items</td>
                    <td className="text-sm text-dark-400">
                      {playlist.last_played ? new Date(playlist.last_played).toLocaleString() : '—'}
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <StatusBadge status={playlist.is_active ? 'active' : 'paused'} size="sm" />
                        <button
                          onClick={() => handleToggleActive(playlist)}
                          className="text-xs text-dark-500 hover:text-dark-300"
                          title={playlist.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {playlist.is_active ? '●' : '○'}
                        </button>
                      </div>
                    </td>
                    <td className="text-right">
                      <Link
                        to={`/playlists/${playlist.id}`}
                        className="text-xs text-dark-400 hover:text-dark-200 mr-1"
                        title="Edit"
                      >
                        ✎
                      </Link>
                      <button
                        onClick={() => {
                          setConfirmItem(playlist);
                          setConfirmOpen(true);
                        }}
                        className="text-xs text-error-400 hover:text-error-300"
                        title="Delete"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Delete Playlist"
        message={`Are you sure you want to delete "${confirmItem?.name}"? This action cannot be undone.`}
        danger
        onConfirm={handleDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </div>
  );
}
